import React from 'react';
import { Code2, Copy, Maximize2, Pencil } from 'lucide-react';
import { RowSidePanel } from './RowSidePanel';
import { InlineCellEditor, type InlineCellEditorContext } from '../../query-editor/components/InlineCellEditor';
import { looksLikeJsonString } from '../utils/jsonDetection';
import { shouldShowExpandIconForQueryResults } from '../../table-explorer/utils/expandedEditDetection';
import type { ResultsGridEditMode } from './ResultsGrid';
import type { CopyValueTransform } from '../hooks/useCellSelection';
import { writeClipboardText } from '../../../../lib/clipboard';

type NormalizedColumn = {
  id: string;
  key: string;
  name: string;
  dataType?: string;
  sourceTable?: string;
  showSourceTable: boolean;
  isPrimaryKey?: boolean;
  isForeignKey?: boolean;
  isLoose?: boolean;
  /** Column is a JSON column (native, declared, or heuristically detected). */
  isJson?: boolean;
  /** Column has a user MetaInfo JSON declaration (drives blue vs muted glyph). */
  isJsonDeclared?: boolean;
  /** Column is an enum column (native engine enum OR MetaInfo-declared). */
  isEnum?: boolean;
  /** Column has a user MetaInfo enum declaration (drives blue vs muted glyph). */
  isEnumDeclared?: boolean;
};

type RowSidePanelContentProps = {
  selectedRowIndex: number | null;
  selectedRow: Record<string, unknown> | null;
  normalizedColumns: NormalizedColumn[];
  editMode?: ResultsGridEditMode;
  sidePanelEditingColumn: string | null;
  setSidePanelEditingColumn: (col: string | null) => void;
  onClose: () => void;
  style: React.CSSProperties;
  openExpandedTextDialog: (title: string, value: string, columnId?: string, rowData?: Record<string, unknown>, sourceRowIndex?: number) => void;
  /**
   * Smart-edit context resolver shared with the main grid — returns the FK /
   * enum / boolean dispatch context for `(columnId, row)` or undefined when
   * the column should fall back to the plain text editor.
   */
  buildSmartContext?: (columnId: string, row: Record<string, unknown>) => InlineCellEditorContext | undefined;
  /**
   * T5 — WKT copy parity: applied to the raw cell value before the "Copy
   * value" button writes to the clipboard. Native-spatial columns resolve to
   * WKT text; every other column passes through untouched. Omit for callers
   * with no spatial columns (identity behavior).
   */
  copyValueTransform?: CopyValueTransform;
};

export const RowSidePanelContent = React.memo(function RowSidePanelContent({
  selectedRowIndex, selectedRow, normalizedColumns, editMode,
  sidePanelEditingColumn, setSidePanelEditingColumn,
  onClose, style, openExpandedTextDialog, buildSmartContext, copyValueTransform,
}: RowSidePanelContentProps) {
  return (
    <RowSidePanel
      title={selectedRowIndex !== null ? `Row ${selectedRowIndex + 1}` : 'Row side panel'}
      onClose={onClose}
      testId="results-row-side-panel"
      style={style}
    >
      {selectedRow ? (
        <div
          className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(200px,1fr))] items-start"
          data-testid="row-side-panel-grid"
        >
          {normalizedColumns.map((column) => (
            <ColumnCard
              key={column.id}
              column={column}
              selectedRow={selectedRow}
              selectedRowIndex={selectedRowIndex}
              editMode={editMode}
              isSidePanelEditing={sidePanelEditingColumn === column.id}
              setSidePanelEditingColumn={setSidePanelEditingColumn}
              openExpandedTextDialog={openExpandedTextDialog}
              buildSmartContext={buildSmartContext}
              copyValueTransform={copyValueTransform}
            />
          ))}
        </div>
      ) : (
        <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
          Select a row to see its values.
        </div>
      )}
    </RowSidePanel>
  );
});

type ColumnCardProps = {
  column: NormalizedColumn;
  selectedRow: Record<string, unknown>;
  selectedRowIndex: number | null;
  editMode?: ResultsGridEditMode;
  isSidePanelEditing: boolean;
  setSidePanelEditingColumn: (col: string | null) => void;
  openExpandedTextDialog: (title: string, value: string, columnId?: string, rowData?: Record<string, unknown>, sourceRowIndex?: number) => void;
  buildSmartContext?: (columnId: string, row: Record<string, unknown>) => InlineCellEditorContext | undefined;
  copyValueTransform?: CopyValueTransform;
};

function ColumnCard({
  column, selectedRow, selectedRowIndex, editMode,
  isSidePanelEditing, setSidePanelEditingColumn, openExpandedTextDialog, buildSmartContext, copyValueTransform,
}: ColumnCardProps) {
  const originalValue = selectedRow[column.key];
  const canSidePanelEdit = editMode?.hasEditableColumns && editMode.isColumnEditable(column.id);

  let sidePanelHasPending = false;
  let sidePanelPendingValue: unknown | undefined;
  if (editMode?.isEditMode && selectedRow) {
    const pk = editMode.getPrimaryKeyForCell(selectedRow, column.id);
    if (pk) {
      sidePanelPendingValue = editMode.getCellPendingValue(pk, column.id);
      sidePanelHasPending = sidePanelPendingValue !== undefined;
    }
  }

  const value = sidePanelHasPending ? sidePanelPendingValue : originalValue;
  const isNull = value === null || value === undefined;
  const isJsonLike = typeof value === 'string' && looksLikeJsonString(value);
  const isObjectValue = !isNull && typeof value === 'object'
    && (Array.isArray(value) || Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null
      || JSON.stringify(value).length >= 80);
  const isLargeText = typeof value === 'string' && !isJsonLike && shouldShowExpandIconForQueryResults(value, {
    isEditMode: editMode?.isEditMode && editMode?.isColumnEditable(column.id),
    dataType: column.dataType,
  });
  const displayValue = (() => {
    if (isNull) return 'NULL';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  })();

  return (
    <div
      className={`rounded-md border bg-background p-2 ${sidePanelHasPending ? 'border-amber-500/50 bg-amber-500/5' : 'border-border'}`}
      data-testid={`side-panel-column-card-${column.id}`}
    >
      <div className="flex items-center gap-1">
        <div className="flex items-baseline gap-1 min-w-0 flex-1">
          {column.isPrimaryKey && (
            <span
              className="text-amber-700 dark:text-amber-400 text-[10px] leading-none flex-shrink-0"
              title="Primary key"
              data-testid="side-panel-column-pk-badge"
            >
              PK
            </span>
          )}
          {column.isForeignKey && (
            <span
              className={
                column.isLoose
                  ? 'text-green-600 dark:text-green-400 text-[10px] leading-none italic flex-shrink-0'
                  : 'text-blue-500 text-[10px] leading-none flex-shrink-0'
              }
              title={column.isLoose ? 'Loose foreign key' : 'Foreign key'}
              data-testid="side-panel-column-fk-badge"
            >
              FK
            </span>
          )}
          {column.isJson && (
            <span
              className={
                column.isJsonDeclared
                  ? 'text-blue-500 text-[10px] leading-none font-mono flex-shrink-0'
                  : 'text-muted-foreground text-[10px] leading-none font-mono flex-shrink-0'
              }
              title={column.isJsonDeclared ? 'Declared JSON column' : 'JSON column'}
              data-testid="side-panel-column-json-badge"
            >
              {'{}'}
            </span>
          )}
          {column.isEnum && (
            <span
              className={
                column.isEnumDeclared
                  ? 'text-blue-500 text-[10px] leading-none font-mono flex-shrink-0'
                  : 'text-muted-foreground text-[10px] leading-none font-mono flex-shrink-0'
              }
              title={column.isEnumDeclared ? 'Declared enum column' : 'Enum column'}
              data-testid="side-panel-column-enum-badge"
            >
              ▾
            </span>
          )}
          <span className="text-xs font-medium truncate min-w-0 shrink" title={column.name}>{column.name}</span>
          {column.showSourceTable && column.sourceTable && (
            <span className="text-[10px] leading-none text-muted-foreground/70 truncate max-w-[120px] shrink" title={column.sourceTable}>
              {column.sourceTable}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {(isJsonLike || isLargeText || isObjectValue) && !isSidePanelEditing && (
            <button
              type="button"
              className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 flex-shrink-0"
              title={isJsonLike || isObjectValue ? 'View JSON' : 'View expanded'}
              aria-label={isJsonLike || isObjectValue ? 'View JSON' : 'View expanded'}
              onClick={() => {
                const strVal = typeof value === 'object' ? JSON.stringify(value) : String(value);
                openExpandedTextDialog(column.name, strVal, column.id, selectedRow, selectedRowIndex ?? undefined);
              }}
            >
              {isJsonLike || isObjectValue ? <Code2 className="h-3 w-3" /> : <Maximize2 className="h-3 w-3" />}
            </button>
          )}
          {canSidePanelEdit && !isSidePanelEditing && (
            <button
              type="button"
              className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 flex-shrink-0"
              title="Edit value"
              data-testid="side-panel-edit-button"
              onClick={() => {
                if (!editMode?.isEditMode) editMode?.onToggleEditMode();
                setSidePanelEditingColumn(column.id);
              }}
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
          {!isNull && !isSidePanelEditing && (
            <button
              type="button"
              className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted/60 flex-shrink-0"
              title="Copy value"
              aria-label="Copy value"
              onClick={() => {
                const copyValue = copyValueTransform ? copyValueTransform(value, column.id) : value;
                void writeClipboardText(typeof copyValue === 'object' ? JSON.stringify(copyValue) : String(copyValue));
              }}
            >
              <Copy className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 text-xs min-w-0">
        {isSidePanelEditing ? (
          <div className="h-7">
            <InlineCellEditor
              variant="side-panel"
              initialValue={value}
              onCommit={(newValue) => {
                editMode!.onCellEdit(selectedRow, column.id, originalValue, newValue);
                setSidePanelEditingColumn(null);
              }}
              onCancel={() => setSidePanelEditingColumn(null)}
              smartContext={buildSmartContext?.(column.id, selectedRow)}
            />
          </div>
        ) : isNull ? (
          <span className="text-muted-foreground/60 italic">NULL</span>
        ) : isJsonLike ? (
          <span className="break-words whitespace-pre-wrap min-w-0 font-mono text-[11px]">{displayValue}</span>
        ) : (
          <span className="break-words whitespace-pre-wrap">{displayValue}</span>
        )}
      </div>
    </div>
  );
}
